"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Analytics from "analytics";

import { type AnalyticsEvents } from "./events";
import loggerPlugin from "./plugins/analyticsLogger";
import { googleAnalyticsPlugin } from "./plugins/googleAnalytics";
import { segmentTrackingPlugin } from "./plugins/segmentTracking";

// Safe function to get Google Analytics ID from environment variables
function getGoogleAnalyticsId(): string | null {
  // Use environment variable that's already configured in layout.tsx
  return process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || null;
}

// Determine environment
const isDevelopment = process.env.NODE_ENV === "development";

// Initialize Analytics only on client side
let AnalyticsTool: ReturnType<typeof Analytics> | null = null;

function getAnalytics() {
  // Only initialize if we're on the client side and haven't initialized yet
  if (typeof window === 'undefined') {
    // Return a no-op analytics object for SSR
    return {
      page: () => Promise.resolve(),
      track: () => Promise.resolve(),
      identify: () => Promise.resolve(),
      reset: () => Promise.resolve(),
    };
  }

  if (!AnalyticsTool) {
    // Configure plugins
    const plugins = [];

    // Add Segment tracking (script loaded by ConsentBasedSegment component)
    plugins.push(segmentTrackingPlugin);

    // Add Google Analytics if measurement ID is available
    const gaId = getGoogleAnalyticsId();
    if (gaId) {
      plugins.push(
        googleAnalyticsPlugin({
          measurementId: gaId,
          debug: isDevelopment,
        })
      );
    }

    // Add logger in development
    if (isDevelopment) {
      plugins.push(loggerPlugin);
    }

    AnalyticsTool = Analytics({
      app: "Wrap.Gifts",
      plugins,
    });
  }

  return AnalyticsTool;
}

async function track<K extends keyof AnalyticsEvents>(
  eventName: K,
  payload?: AnalyticsEvents[K],
) {
  return getAnalytics().track(eventName, payload);
}

export const analytics = {
  page: () => getAnalytics().page(),
  track: track,
  identify: (userId: string, traits?: Record<string, any>) => getAnalytics().identify(userId, traits),
  reset: () => getAnalytics().reset(),
};

export function PageAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    analytics.page().catch(console.error);
  }, [pathname, searchParams]);

  return null;
}
